import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getSkillDef } from '@shared/data/skills';
import { createUIIcon, formatThousands } from '../../sprites/IconAtlases';

/**
 * Feed de experiencia de habilidades, estilo "XP drops" de MMORPG (OSRS/RS): al
 * ganar XP en una skill aparece un renglón `[ícono] +N <Skill>` a la derecha que
 * flota hacia arriba y se desvanece. Si seguís ganando XP de la MISMA skill, el
 * renglón se queda y su número se acumula (batch), como en OSRS. Fijo a la cámara.
 *
 * Ver docs/SKILLS_SYSTEM.md.
 */

const FLOAT_DISTANCE = 46; // px que sube el renglón
const LIFE_MS = 2600; // duración antes de desvanecerse
const ROW_STAGGER = 26; // separación vertical entre renglones simultáneos

interface FeedEntry {
  container: Phaser.GameObjects.Container;
  text: Phaser.GameObjects.Text;
  amount: number;
  tween?: Phaser.Tweens.Tween;
}

export class SkillXpFeed {
  private entries = new Map<string, FeedEntry>();
  private baseX = 0;
  private baseY = 0;

  constructor(private scene: Phaser.Scene) {
    this.computeAnchor();
    scene.scale.on('resize', () => this.computeAnchor());
  }

  private computeAnchor(): void {
    const cam = this.scene.cameras.main;
    // Zona derecha, ~a un tercio desde arriba; los renglones flotan hacia arriba.
    this.baseX = Math.round(cam.width - 220);
    this.baseY = Math.round(cam.height * 0.34);
  }

  /** Registra una ganancia de XP en una skill (la muestra o acumula). */
  public push(skillId: string, amount: number): void {
    if (amount <= 0) return;
    const def = getSkillDef(skillId);
    const name = def?.name ?? skillId;

    const existing = this.entries.get(skillId);
    if (existing) {
      // Acumular en el renglón activo y reiniciar su vida/posición.
      existing.amount += amount;
      existing.text.setText(`+${formatThousands(existing.amount)} ${name}`);
      this.animate(skillId, existing);
      return;
    }

    // Nuevo renglón. Se ubica desplazado según cuántos hay activos.
    const y = this.baseY - this.entries.size * ROW_STAGGER;
    const container = this.scene.add.container(this.baseX, y);
    container.setScrollFactor(0).setDepth(9500);

    const icon = createUIIcon(this.scene, 0, 0, def?.icon ?? 'experience', 18);
    if (icon) container.add(icon);
    const text = this.scene.add
      .text(14, 0, `+${formatThousands(amount)} ${name}`, {
        fontFamily: GAME_FONT_FAMILY,
        fontSize: '15px',
        color: '#8fd06a',
        stroke: '#0a1006',
        strokeThickness: 3,
      })
      .setOrigin(0, 0.5);
    container.add(text);

    const entry: FeedEntry = { container, text, amount };
    this.entries.set(skillId, entry);
    this.animate(skillId, entry);
  }

  /** (Re)inicia la animación de flotar+desvanecer de un renglón. */
  private animate(skillId: string, entry: FeedEntry): void {
    entry.tween?.remove();
    const startY = this.baseY - this.indexOf(skillId) * ROW_STAGGER;
    entry.container.setPosition(this.baseX, startY).setAlpha(1);
    entry.tween = this.scene.tweens.add({
      targets: entry.container,
      y: startY - FLOAT_DISTANCE,
      alpha: { from: 1, to: 0 },
      ease: 'Sine.easeIn',
      duration: LIFE_MS,
      onComplete: () => {
        entry.container.destroy();
        this.entries.delete(skillId);
      },
    });
  }

  /** Índice de orden del skill entre los activos (para el stagger vertical). */
  private indexOf(skillId: string): number {
    let i = 0;
    for (const key of this.entries.keys()) {
      if (key === skillId) return i;
      i++;
    }
    return i;
  }

  public destroy(): void {
    for (const entry of this.entries.values()) {
      entry.tween?.remove();
      entry.container.destroy();
    }
    this.entries.clear();
  }
}
